'use client'

import { Chip, Media, Container, Link } from '@portfolio/components'
import { SectionArticleRelatedProps } from '@portfolio/models'
import { styled } from '@portfolio/styles'
import { useTranslation } from 'react-i18next'

const ArticlesGrid = styled('div', {
  margin: '0 auto',
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '$56',
  padding: '$96 0 $48',

  '@md': {
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '$64 $24',
    maxWidth: '100%',
  },
})

const Article = styled('div', {
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'grid',
  alignItems: 'start',
  gridTemplateRows: 'auto 1fr',
})

const ArticleTitle = styled('h3', {
  projectFont: 'heading07',
  gridRow: 2,
  gridColumn: 1,
})

const ArticleGridContent = styled('div', {
  position: 'relative',
  display: 'grid',
  rowGap: '$24',
  width: '100%',
  paddingTop: '$20',

  '@md': {
    rowGap: '$32',
    height: '100%',
    gridTemplateRows: 'auto auto 1fr',
  },
})

const MediaWrap = styled('div', {
  gridColumn: 1,
  gridRow: 1,
  borderRadius: '$r2',
  overflow: 'hidden',
})

const ReadArticle = styled('span', {
  projectFont: 'heading08',
  textDecoration: 'underline',
  gridColumn: 1,
  gridRow: 3,
  textUnderlineOffset: '.35em',
  display: 'grid',
  height: '100%',
  alignItems: 'end',
})

const ArticleTags = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gridColumn: 1,
  gridRow: 1,
  columnGap: '$12',
})

const ArticleTag = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '$10',
  zIndex: '$content',
})

const LinkLayer = styled(Link, {
  position: 'absolute',
  width: '100%',
  height: '100%',
  cursor: 'pointer',
  pointerEvents: 'all',
})

export const SectionArticleRelated = ({
  relatedArticles,
  priority,
}: SectionArticleRelatedProps) => {
  const { t } = useTranslation('articles')

  return (
    <Container>
      <ArticlesGrid>
        {relatedArticles &&
          relatedArticles.map((article, index: number) => (
            <Article key={article.path}>
              {article.thumbnail && (
                <MediaWrap>
                  <Media
                    {...article.thumbnail}
                    hardcropRatio={'landscape3'}
                    priority={priority && index < 3}
                    sizes="(min-width: 740px) 50vw, 100vw"
                  />
                </MediaWrap>
              )}
              <ArticleGridContent>
                {article.tags && (
                  <ArticleTags>
                    <ArticleTag>
                      {article.tags.map((tag) => (
                        <Chip
                          key={tag.path}
                          title={tag.title}
                          type="dark"
                          href={tag.path}
                        />
                      ))}
                    </ArticleTag>
                  </ArticleTags>
                )}
                {article.title && <ArticleTitle>{article.title}</ArticleTitle>}

                <ReadArticle>{t('readArticle')}</ReadArticle>
              </ArticleGridContent>
              {article.path && (
                <LinkLayer
                  href={article.path}
                  tabIndex={-1}
                  aria-label={`Article - ${article.title}`}
                  key={article.path}
                />
              )}
            </Article>
          ))}
      </ArticlesGrid>
    </Container>
  )
}
